import type { SessionListItem } from './sessionList';

export type SessionSelectionTreeItem = SessionListItem;

export type SessionSelectionTreeSession = {
  session: SessionListItem;
  timeLabel: string;
};

export type SessionSelectionTreeDriverGroup = {
  key: string;
  label: string;
  avatarUrl: string | null;
  sessions: SessionSelectionTreeSession[];
};

export type SessionSelectionTreeDateGroup = {
  key: string;
  label: string;
  sortMs: number;
  drivers: SessionSelectionTreeDriverGroup[];
};

const UNKNOWN_DATE_KEY = 'unknown-date';

function sessionDateKey(recordedAtMs: number | null) {
  if (recordedAtMs === null || !Number.isFinite(recordedAtMs)) return UNKNOWN_DATE_KEY;
  const date = new Date(recordedAtMs);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function sessionDateLabel(recordedAtMs: number | null) {
  if (recordedAtMs === null || !Number.isFinite(recordedAtMs)) return 'Date unavailable';
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(recordedAtMs);
}

function sessionTimeLabel(session: SessionListItem) {
  if (session.recordedAtMs !== null && Number.isFinite(session.recordedAtMs)) {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    }).format(session.recordedAtMs);
  }

  const subtitleTime = session.subtitle.split('·')[0]?.trim();
  return subtitleTime && /\d/.test(subtitleTime) ? subtitleTime : 'Time unavailable';
}

function sessionDriverLabel(session: SessionListItem) {
  const name = session.driverName?.trim();
  return name || 'Driver unassigned';
}

function sessionDriverKey(session: SessionListItem) {
  return session.driverId
    ? `id:${session.driverId}`
    : `name:${sessionDriverLabel(session).toLocaleLowerCase()}`;
}

export function buildSessionSelectionTree(
  sessions: SessionListItem[],
): SessionSelectionTreeDateGroup[] {
  const dates = new Map<
    string,
    {
      label: string;
      sortMs: number;
      drivers: Map<string, SessionSelectionTreeDriverGroup>;
    }
  >();

  for (const session of sessions) {
    const dateKey = sessionDateKey(session.recordedAtMs);
    const date = dates.get(dateKey) ?? {
      label: sessionDateLabel(session.recordedAtMs),
      sortMs: session.recordedAtMs ?? Number.NEGATIVE_INFINITY,
      drivers: new Map<string, SessionSelectionTreeDriverGroup>(),
    };
    date.sortMs = Math.max(date.sortMs, session.recordedAtMs ?? Number.NEGATIVE_INFINITY);

    const driverKey = sessionDriverKey(session);
    const driver = date.drivers.get(driverKey) ?? {
      key: `${dateKey}:${driverKey}`,
      label: sessionDriverLabel(session),
      avatarUrl: session.driverAvatarUrl,
      sessions: [],
    };
    driver.sessions.push({ session, timeLabel: sessionTimeLabel(session) });
    if (!driver.avatarUrl && session.driverAvatarUrl) driver.avatarUrl = session.driverAvatarUrl;
    date.drivers.set(driverKey, driver);
    dates.set(dateKey, date);
  }

  return [...dates.entries()]
    .map(([key, date]) => ({
      key,
      label: date.label,
      sortMs: date.sortMs,
      drivers: [...date.drivers.values()]
        .map((driver) => ({
          ...driver,
          sessions: [...driver.sessions].sort(
            (left, right) =>
              (right.session.recordedAtMs ?? Number.NEGATIVE_INFINITY) -
                (left.session.recordedAtMs ?? Number.NEGATIVE_INFINITY) ||
              right.session.id.localeCompare(left.session.id),
          ),
        }))
        .sort((left, right) => {
          const leftUnassigned = left.label === 'Driver unassigned';
          const rightUnassigned = right.label === 'Driver unassigned';
          if (leftUnassigned !== rightUnassigned) return leftUnassigned ? 1 : -1;
          return left.label.localeCompare(right.label);
        }),
    }))
    .sort((left, right) => right.sortMs - left.sortMs || right.key.localeCompare(left.key));
}

export function updateSessionSelection(
  selectedSessionIds: string[],
  sessionId: string,
  checked: boolean,
) {
  const selectedIds = [...new Set(selectedSessionIds)];
  const selectedIndex = selectedIds.indexOf(sessionId);
  if (checked) {
    return selectedIndex >= 0 ? selectedIds : [...selectedIds, sessionId];
  }
  if (selectedIndex < 0) return selectedIds;
  if (selectedIndex === 0 && selectedIds.length === 1) return selectedIds;
  return selectedIds.filter((id) => id !== sessionId);
}

export function makeSessionPrimary(selectedSessionIds: string[], sessionId: string) {
  const selectedIds = [...new Set(selectedSessionIds)];
  return [sessionId, ...selectedIds.filter((id) => id !== sessionId)];
}
